import { useEffect, useState } from 'react';
import { connectorAPI, ConnectorAccess } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Whether the signed-in user may use MCP connectors (admin-granted per user;
 * admins have it unless another admin hid it). Cached per user like
 * useTaskAccess; call {@link invalidateConnectorAccess} after a change that
 * affects it. A failed load counts as "no access" and is not cached.
 */
let cache: { userId: number; promise: Promise<ConnectorAccess | null> } | null = null;

export const invalidateConnectorAccess = () => {
  cache = null;
};

export const useConnectorAccess = (): ConnectorAccess | null => {
  const { user } = useAuth();
  const [access, setAccess] = useState<ConnectorAccess | null>(null);

  useEffect(() => {
    if (!user) {
      setAccess(null);
      return;
    }
    if (!cache || cache.userId !== user.userId) {
      const request = connectorAPI.getAccess().then(r => r.data);
      const entry = { userId: user.userId, promise: request.catch(() => null) };
      cache = entry;
      // Drop a failed load so the next mount asks again.
      request.catch(() => { if (cache === entry) cache = null; });
    }
    let alive = true;
    cache.promise.then(a => { if (alive) setAccess(a); });
    return () => { alive = false; };
  }, [user]);

  return access;
};
